// ©2024 Austin App House. All rights reserved.
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { SubmissionError, submit } from 'redux-form';
import { toggleModal } from '../../storage/actions/modalsActions';
import CreateClientApprovalCodeReduxForm from '../layout/reduxForm/CreateClientApprovalCode';
import { createApprovalCode } from '../../storage/actions/workOrdersActions';
import getApprovalCodesList from '../../utils/api/get/getApprovalCodesList';
import CustomSelect from '../layout/CustomSelect';
import { dashboardSearchable, dashboardSelectClassNames } from '../../utils/customSelectStyle';
import DashboardModalShell from './DashboardModalShell';

const CreateOrAddClientApprovalCodeToWorkOrder = ({
  modalState,
  toggleModal,
  submit,
}) => {
  const [approvalCodes, setApprovalCodes] = useState([]);
  const [selectedApprovalCode, setSelectedApprovalCode] = useState();

  const createApprovalCodesOptionsForSelect = list => (
    list.map(approvalCode => ({
      value: approvalCode._id,
      label: approvalCode.name,
      required: approvalCode.required,
    }))
  );

  // eslint-disable-next-line consistent-return
  const fetchClientApprovalCodes = async () => {
    const newApprovalCodes = await getApprovalCodesList();
    if (!(modalState.type?.input.value?.length > 0)) {
      return setApprovalCodes(
        createApprovalCodesOptionsForSelect(newApprovalCodes),
      );
    }
    const selectedApprovalCodesIds = modalState.type?.input.value.map(item => (
      item._id || item.value
    ));
    setApprovalCodes(
      createApprovalCodesOptionsForSelect(
        newApprovalCodes.filter(customField => !selectedApprovalCodesIds.includes(
          customField._id,
        )),
      ),
    );
  };

  useEffect(() => {
    fetchClientApprovalCodes();
  }, []);

  const closeModal = () => {
    toggleModal(false, 'createOrAddClientApprovalCodeToWorkOrder');
  };

  const getApprovalCodeInfo = async (name, required, dispatch) => {
    if (selectedApprovalCode?.value) {
      return ({
        approvalCodeId: selectedApprovalCode.value,
        name: selectedApprovalCode.label,
      });
    }
    const result = await dispatch(createApprovalCode(name, required));
    if (!result.success) throw new SubmissionError({ _error: result.message });
    return ({
      approvalCodeId: result?.payload?.newDocument?._id,
      name: result?.payload?.newDocument?.name,
    });
  };

  const onSubmit = async (values, dispatch) => {
    const {
      name,
      isRequired,
    } = values;
    const approvalCodeInfo = await getApprovalCodeInfo(name, isRequired.value, dispatch);
    addApprovalCodeToWorkOrder({
      approvalCodeId: approvalCodeInfo.approvalCodeId,
      name: approvalCodeInfo.name,
      required: isRequired.value,
    });
    return {};
  };

  const handlerSubmit = event => {
    event.preventDefault();
    submit('createClientApprovalCodeReduxForm');
  };

  const addApprovalCodeToWorkOrder = approvalCode => {
    modalState.type.input.onChange([...modalState.type?.input.value, approvalCode]);
    closeModal();
  };

  return (
    <DashboardModalShell
      isOpen={!!modalState?.isOpen}
      onClose={closeModal}
      title="Add Client Approval Code"
      titleId="add-client-approval-code-title"
      bodyClassName="wurqly-modal-scroll dashboard-modal-form-scroll"
      footer={(
        <>
          <button
            type="button"
            className="dashboard-btn dashboard-btn--primary"
            onClick={handlerSubmit}
          >
            Save
          </button>
          <button
            type="button"
            className="dashboard-btn dashboard-btn--secondary"
            onClick={closeModal}
          >
            Cancel
          </button>
        </>
      )}
    >
      <div className="field_col">
        <label className="field_name" htmlFor="approval_code_select">Select Approval Code</label>
        <div className="field_block dashboard-select-wrap">
          <CustomSelect
            inputId="approval_code_select"
            isClearable
            isSearchable
            options={approvalCodes}
            value={selectedApprovalCode}
            styles={dashboardSearchable}
            classNames={dashboardSelectClassNames}
            onChange={setSelectedApprovalCode}
            placeholder="Select Approval Code"
          />
        </div>
      </div>

      <div className="separate">OR</div>

      <CreateClientApprovalCodeReduxForm
        enableReinitialize
        onSubmit={onSubmit}
        disabled={!!selectedApprovalCode?.value}
        initialValues={selectedApprovalCode?.value ? { name: selectedApprovalCode.label, isRequired: selectedApprovalCode.required } : {}}
      />
    </DashboardModalShell>
  );
};

const mapDispatchToProps = {
  toggleModal,
  submit,
};

const mapStateToProps = state => ({
  modalState: state.modalState.createOrAddClientApprovalCodeToWorkOrder,
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateOrAddClientApprovalCodeToWorkOrder);
